import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import ReactGA from 'react-ga4';
import AppErrorBoundary from './components/AppErrorBoundary';
import LoadingFallback from './components/LoadingFallback';
import CookieConsentBanner from './components/CookieConsentBanner';
import { hasAnalyticsConsent } from './utils/consentManager';

// Code splitting: lazy load route components
const HomePage = lazy(() => import('./pages/HomePage'));
const GamePage = lazy(() => import('./pages/GamePage'));
const ResultsPage = lazy(() => import('./pages/ResultsPage'));
const DailyChallengePage = lazy(() => import('./pages/DailyChallengePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LessonsPage = lazy(() => import('./pages/LessonsPage'));
const LessonViewPage = lazy(() => import('./pages/LessonViewPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const PrivacySettingsPage = lazy(() => import('./pages/PrivacySettingsPage'));

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_ID || '';

// Component to track page views (only with consent)
function AnalyticsTracker() {
  const location = useLocation();
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    // Check initial consent
    setConsent(hasAnalyticsConsent());

    // Listen for consent changes
    const handleConsentChange = (e) => {
      setConsent(e.detail?.analytics ?? false);
    };

    window.addEventListener('consentChanged', handleConsentChange);
    return () => window.removeEventListener('consentChanged', handleConsentChange);
  }, []);

  useEffect(() => {
    if (!GA_MEASUREMENT_ID || !consent) {
      return;
    }
    // Only track in production and with consent
    if (process.env.NODE_ENV === 'production') {
      ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    }
  }, [location, consent]);

  return null;
}

function App() {
  const isProduction = process.env.NODE_ENV === 'production';
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false);

  // Initialize Google Analytics only with consent
  useEffect(() => {
    if (!isProduction || !GA_MEASUREMENT_ID) {
      return;
    }

    const initializeAnalytics = () => {
      if (hasAnalyticsConsent() && !analyticsInitialized) {
        ReactGA.initialize(GA_MEASUREMENT_ID);
        setAnalyticsInitialized(true);
      }
    };

    // Check initial consent
    initializeAnalytics();

    // Listen for consent changes
    const handleConsentChange = () => {
      initializeAnalytics();
    };

    window.addEventListener('consentChanged', handleConsentChange);
    return () => window.removeEventListener('consentChanged', handleConsentChange);
  }, [isProduction, analyticsInitialized]);

  return (
    <Router>
      <AppErrorBoundary>
        <AnalyticsTracker />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/game" element={<GamePage />} />
            <Route path="/results" element={<ResultsPage />} />
            <Route path="/daily-challenge" element={<DailyChallengePage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/lessons" element={<LessonsPage />} />
            <Route path="/lesson/:lessonId" element={<LessonViewPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-settings" element={<PrivacySettingsPage />} />
          </Routes>
        </Suspense>
        <CookieConsentBanner />
        {isProduction && hasAnalyticsConsent() && <Analytics />}
      </AppErrorBoundary>
    </Router>
  );
}

export default App;
